import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FREE_SUBSCRIPTION, PlanFeature, PlanTier, Subscription } from '../models/subscription.model';

/**
 * Holds the signed-in company's subscription for the lifetime of the session.
 *
 * The plan belongs to a company, and the server knows which one only from the token, so it is
 * read when a session starts: at startup when a token is already there (`APP_INITIALIZER`), and
 * right after signing in, before the first screen (`AuthService.loadSessionContext`). It used to
 * be read only at startup - before signing in, when the request is refused - so a fresh sign-in
 * kept the free plan's defaults and hid every paid module (mission orders, expenses...) until
 * the page was reloaded. Guards and menus then answer synchronously; the server enforces the
 * same matrix on every request, so this keeps the UI honest rather than securing anything.
 */
@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private http = inject(HttpClient);

  private apiUrl = `${environment.apiUrl}/v1.0/subscription`;

  private subscriptionSubject = new BehaviorSubject<Subscription>(FREE_SUBSCRIPTION);
  readonly subscription$: Observable<Subscription> = this.subscriptionSubject.asObservable();

  private isInitialized = false;

  /**
   * Loads the subscription on app startup. Never throws: like the configuration
   * initializer, a failure here must not stop the app from booting — it just
   * leaves the free-tier defaults in place.
   */
  async initializeSubscription(): Promise<void> {
    try {
      const subscription = await firstValueFrom(this.http.get<Subscription>(this.apiUrl));
      this.subscriptionSubject.next(subscription);
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to load subscription, falling back to the free plan:', error);
      this.subscriptionSubject.next(FREE_SUBSCRIPTION);
      this.isInitialized = false;
    }
  }

  /**
   * Back to the free-plan defaults, for when nobody is signed in: the plan belongs to the
   * signed-in user's company, and the next person to sign in may work for another one.
   */
  reset(): void {
    this.subscriptionSubject.next(FREE_SUBSCRIPTION);
    this.isInitialized = false;
  }

  /** Re-reads the subscription — call after anything that changes the headcount. */
  async refresh(): Promise<void> {
    await this.initializeSubscription();
  }

  get snapshot(): Subscription {
    return this.subscriptionSubject.value;
  }

  tier(): PlanTier {
    return this.snapshot.plan;
  }

  isFeatureEnabled(feature?: PlanFeature): boolean {
    if (!feature) return true;
    return this.snapshot.features[feature] === true;
  }

  /** True once the real subscription has been read from the server. */
  isLoaded(): boolean {
    return this.isInitialized;
  }

  /** True when adding another employee would exceed the plan's cap. */
  isEmployeeLimitReached(): boolean {
    const { maxEmployees, currentEmployees } = this.snapshot;
    if (maxEmployees === null || maxEmployees === undefined) return false;
    return currentEmployees >= maxEmployees;
  }
}
